import axios, { AxiosError, AxiosRequestConfig } from "axios";
import type { ApmBase } from "@elastic/apm-rum";
import { ApmContextService } from "../services/apm/apm-context.service";
import { rumTracing } from "../services/apm/rum-tracing.service";

function safeUrlFromConfig(config?: AxiosRequestConfig): string {
  const baseURL = (config?.baseURL || "").toString();
  const url = (config?.url || "").toString();
  if (!baseURL) return url;
  if (!url) return baseURL;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${baseURL.replace(/\/+$/, "")}/${url.replace(/^\/+/, "")}`;
}

export function setupApmAxiosInterceptors(apm: ApmBase, apmContext: ApmContextService): void {
  axios.interceptors.request.use((config) => {
    const route = window.location?.pathname || "/";
    const { tenantId, profileId } = apmContext.getTraceContextHeaders();

    const tracestateEntries: Record<string, string | undefined> = {
      route,
      tenant: tenantId,
      profile: profileId,
    };
    const tracestate = rumTracing.buildTracestate(tracestateEntries);

    const method = (config.method || "GET").toUpperCase();
    const requestUrl = safeUrlFromConfig(config) || config.url || "";

    if (apm?.isActive?.()) {
      const span = apm.startSpan(`HTTP ${method} ${requestUrl}`, "external.http");
      (config as any).__apmSpan = span;
      if (tracestate) {
        config.headers = { ...(config.headers as any), tracestate };
      }
      return config;
    }

    const traceparent = rumTracing.generateTraceparent();
    config.headers = {
      ...(config.headers as any),
      traceparent,
      ...(tracestate ? { tracestate } : {}),
    };
    return config;
  });

  axios.interceptors.response.use(
    (response) => {
      const span = (response.config as any)?.__apmSpan;
      span?.end?.();
      return response;
    },
    (error: AxiosError) => {
      const config = error.config as any;
      const span = config?.__apmSpan;
      const method = (config?.method || "GET").toUpperCase();
      const requestUrl = safeUrlFromConfig(config) || config?.url || "";
      const status = error.response?.status;
      const statusText = error.response?.statusText;

      if (apm?.isActive?.()) {
        apm.captureError(`HTTP Error: ${method} ${requestUrl}`, {
          labels: {
            url: requestUrl,
            method,
            status,
            statusText,
          },
        } as any);
      }

      span?.end?.();
      return Promise.reject(error);
    }
  );
}

